import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import Multiclamp from 'react-multi-clamp';

import ArrowDown from '@crehana/crehana-icons/lib/ArrowDown';

import useOnClickOutside from './useOnClickOutside';
import transition from '../styles/transition';
import parseClassnames from '../helpers/parseClassnames';
import Label from '../Label';
import T from '../Text';
import { OptionType } from '../Checkbox/types';
import { ROOT, SM_ROOT, WRAPPER, MAIN_BOX } from './classnames';
import { DropdownProps } from './types';
import Menu from '../Menu';

const arrowStyles = {
  bottom: (open: boolean) =>
    open ? 'transform: rotate(-180deg);' : 'transform: rotate(0)',
  top: (open: boolean) =>
    !open ? 'transform: rotate(-180deg);' : 'transform: rotate(0)',
};

const Arrow = styled(ArrowDown)<{
  open: boolean;
  displayPosition: 'bottom' | 'top' | 'responsive';
}>`
  transition: ${transition('transform')};
  ${({ theme, open, displayPosition }) =>
    displayPosition === 'responsive'
      ? `
    ${arrowStyles.top(open)}
    ${theme.breakpoints.up('sm')} {
      ${arrowStyles.bottom(open)}
    }
  `
      : arrowStyles[displayPosition](open)}}
`;

const Dropdown: FunctionComponent<DropdownProps> = ({
  options,
  selected,
  onSelected,
  label,
  hint,
  required,
  disabled,
  full,
  sm,
  size = 4,
  open: openProp,
  onToggleOpen: onToggleOpenProp,
  selectId,
  name,
  form,
  className = '',
  check,
  clampText,
  dark,
  onOpen,
  datatestid,
  'data-testid': dataTestId,
  displayPosition = 'bottom',
  inputRef,
  ...other
}) => {
  const OPTION_HEIGHT = sm ? 34 : 56;
  const [openState, setOpenState] = useState<boolean>(false);
  const containerRef = useRef<HTMLElement>(null);

  const setSelectElementValue = (newValue: string) => {
    if (selectId) {
      const selectElement = document.getElementById(selectId);

      if (selectElement) {
        (selectElement as HTMLSelectElement).value = newValue;
      }
    } else if (name) {
      const namedElements = Array.from(document.getElementsByName(name));
      const element = namedElements.find(e => e.tagName === 'SELECT');

      if (element) {
        (element as HTMLSelectElement).value = newValue;
      }
    }
  };

  useEffect(() => {
    if (selected && (selectId || name)) {
      setSelectElementValue(selected.value.toString());
    }
  }, []);

  const hasOpenProp = typeof openProp === 'boolean';

  const onToggleOpen = () => {
    const newOpenState = !openState;

    if (!hasOpenProp) {
      setOpenState(newOpenState);
      if (newOpenState && onOpen) {
        onOpen();
      }
    }
    if (typeof onToggleOpenProp === 'function') {
      onToggleOpenProp(!hasOpenProp ? newOpenState : undefined);
    }
  };

  const handler = () => {
    if (openState || openProp) {
      onToggleOpen();
    }
  };

  useOnClickOutside(containerRef, handler);

  const onSelect = (option: OptionType) => {
    const currentlySelected = selected && selected.value === option.value;
    let newSelected: OptionType | undefined = option;

    if (!required && currentlySelected) {
      newSelected = undefined;
    }

    if (typeof onSelected === 'function' && selected !== newSelected) {
      onSelected(newSelected);
    }
    if (selectId || name) {
      setSelectElementValue(selected ? selected.value.toString() : '');
    }
    onToggleOpen();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.keyCode === 13) {
      event.stopPropagation();
      action();
    }
  };

  const open = typeof openProp === 'boolean' ? openProp : openState;

  let styleType = sm ? 'sm' : 'default';

  if (open) {
    styleType = 'open';
  }
  if (disabled) {
    styleType = 'disabled';
  }

  const shownOptions = options.length >= size ? size : options.length;
  const selectedLabel = selected ? selected.label : '';

  const wrapperPositionClassNames = {
    bottom: 'flex-col',
    top: 'flex-col-reverse absolute bottom-0',
    responsive:
      'flex-col-reverse absolute bottom-0 sm:flex-col sm:bottom-auto sm:top-0',
  };

  return (
    <div
      className={parseClassnames(
        className,
        cn(
          sm ? SM_ROOT : ROOT,
          full ? 'w-full' : 'w-full sm:w-280',
          'relative',
        ),
      )}
      {...other}
      ref={containerRef}
    >
      <div
        className={cn(
          WRAPPER[check ? 'check' : styleType](dark),
          dark ? 'bg-base-dark' : 'bg-white',
          wrapperPositionClassNames[displayPosition],
        )}
        style={{
          transition: transition(['box-shadow', 'background-color', 'border']),
          height: `${
            open
              ? OPTION_HEIGHT * (shownOptions + 1)
              : OPTION_HEIGHT + (!dark ? 2 : 0)
          }px`,
          zIndex: open ? 200 : 2,
        }}
      >
        <select
          className="hidden"
          name={name}
          id={selectId}
          form={form}
          data-testid={dataTestId || datatestid}
        >
          {(label || hint) && (
            <option value="" disabled>
              {label || hint}
            </option>
          )}
          {options.map(opt => (
            <option key={`${selectId || name}-${opt.value}`} value={opt.value}>
              {opt.hiddenLabel || opt.label}
            </option>
          ))}
        </select>
        <div
          className={MAIN_BOX[styleType](dark)}
          onClick={onToggleOpen}
          onKeyDown={e => handleKeyDown(e, onToggleOpen)}
          data-testid={`${dataTestId || datatestid}-button`}
        >
          {(label || hint) && (
            <Label
              label={label! || hint!}
              floating={!!label}
              focused={!!selected}
              disabled={disabled}
              sm={sm}
              darkMode={dark}
            />
          )}
          <T
            className={cn('overflow-hidden pr-16', {
              'break-all': sm || clampText,
              'opacity-80': disabled,
              'max-h-24': !label,
              'pt-16 max-h-40': label && !!selected,
            })}
            color="inherit"
            data-testid={`${dataTestId || datatestid}-label`}
          >
            {clampText && selectedLabel ? (
              <Multiclamp clamp={1}>{selectedLabel}</Multiclamp>
            ) : (
              selectedLabel
            )}
          </T>
          <div className="w-12 h-12">
            <Arrow
              className={cn('fill-current', {
                [disabled
                  ? 'text-base-lighter opacity-80'
                  : 'text-primary-main']: !dark,
                [disabled ? 'text-gray-dark' : 'text-base-lighter']: dark,
              })}
              ignoreColor
              size={12}
              displayPosition={displayPosition}
              open={open}
            />
          </div>
        </div>
        {open && (
          <Menu
            options={options}
            selected={selected}
            onSelected={onSelect}
            full
            clampText={clampText}
            dark={dark}
            data-testid={`${dataTestId || datatestid}-options`}
          />
        )}
      </div>
    </div>
  );
};

export default Dropdown;
